/**
 * Onboarding Sessions — IN-2.a
 * ────────────────────────────
 * Pure writers for OnboardingSession state.
 *
 * Hard rules:
 *   • Token consumption is the only way to create a session.
 *   • One active (non-terminal) session per token.
 *   • `approved` and `rejected` are terminal.
 *   • `reupload_requested` → caller may flip back to `draft` via
 *      updateOnboardingDraft / completeOnboardingStep.
 *   • No production Contractor mutation here. (IN-2.g.)
 *   • No raw bank data fields permitted in draft.
 */

import {
  MIN_REASON_LENGTH,
  type ContractorOnboardingDraft,
  type OnboardingInviteToken,
  type OnboardingSession,
  type OnboardingSessionStatus,
} from "./types";

function nowIso(): string {
  return new Date().toISOString();
}

let __seq = 0;
function nextId(): string {
  __seq += 1;
  return `oses-${Date.now().toString(36)}-${__seq.toString(36)}`;
}

function requireNonEmpty(v: string | undefined, field: string): string {
  const t = (v ?? "").trim();
  if (!t) throw new Error(`onboardingSessions: ${field} is required.`);
  return t;
}

// ── Raw-bank guard ─────────────────────────────────────────────────
const FORBIDDEN_DRAFT_KEYS = new Set([
  "routingNumber",
  "routing_number",
  "accountNumber",
  "account_number",
  "iban",
  "swift",
  "ssn",
  "social_security_number",
  "vettingStatus",
  "executionScore",
  "isOnProbation",
  "suspensionLog",
  // IN-2.f — parent binding is locked at token creation. Invitees
  // cannot rewrite it via draft patches (prevents parent spoofing /
  // orphaning).
  "parentEntityId",
  "entityType",
  "accountabilityMode",
]);

function assertDraftSafe(patch: Partial<ContractorOnboardingDraft>): void {
  for (const key of Object.keys(patch)) {
    if (FORBIDDEN_DRAFT_KEYS.has(key)) {
      throw new Error(
        `onboardingSessions: forbidden draft field "${key}" — onboarding cannot carry raw bank or vetting state.`,
      );
    }
  }
}

// ── Create from consumed token ─────────────────────────────────────
export function createSessionFromToken(
  sessions: OnboardingSession[],
  token: OnboardingInviteToken,
  initialStep = "identity",
): { sessions: OnboardingSession[]; session: OnboardingSession } {
  if (token.status !== "consumed") {
    throw new Error(
      "onboardingSessions: cannot create a session from a non-consumed token.",
    );
  }
  const existing = sessions.find(
    (s) => s.tokenId === token.id && s.status !== "rejected",
  );
  if (existing) {
    return { sessions, session: existing };
  }
  const now = nowIso();
  const session: OnboardingSession = {
    id: nextId(),
    contractorId: token.contractorId,
    tokenId: token.id,
    entityType: token.entityType,
    parentEntityId: token.parentId,
    currentStep: initialStep,
    completedSteps: [],
    status: "draft",
    draft: {
      entityType: token.entityType,
      parentEntityId: token.parentId,
    },
    startedAt: now,
    lastTouchedAt: now,
  };
  return { sessions: [...sessions, session], session };
}

function patchSession(
  sessions: OnboardingSession[],
  sessionId: string,
  fn: (s: OnboardingSession) => OnboardingSession,
): OnboardingSession[] {
  const target = sessions.find((s) => s.id === sessionId);
  if (!target) {
    throw new Error(`onboardingSessions: session ${sessionId} not found.`);
  }
  return sessions.map((s) => (s.id === sessionId ? fn(s) : s));
}

function assertWritable(s: OnboardingSession): void {
  if (s.status === "approved" || s.status === "rejected") {
    throw new Error(
      `onboardingSessions: session ${s.id} is terminal (${s.status}); not writable.`,
    );
  }
}

function isComplianceRequirementSettingsOnlyPatch(
  patch: Partial<ContractorOnboardingDraft>,
): boolean {
  const keys = Object.keys(patch);
  return (
    keys.length === 1 &&
    keys[0] === "complianceRequirementSettings" &&
    patch.complianceRequirementSettings !== undefined
  );
}

export function updateOnboardingDraft(
  sessions: OnboardingSession[],
  sessionId: string,
  patch: Partial<ContractorOnboardingDraft>,
  actor: string,
): OnboardingSession[] {
  requireNonEmpty(actor, "actor");
  assertDraftSafe(patch);
  return patchSession(sessions, sessionId, (s) => {
    const complianceSettingsOnly = isComplianceRequirementSettingsOnlyPatch(patch);
    if (!complianceSettingsOnly) {
      assertWritable(s);
    }
    return {
      ...s,
      status: s.status === "reupload_requested" ? "draft" : s.status,
      draft: { ...s.draft, ...patch },
      lastTouchedAt: nowIso(),
    };
  });
}

export function completeOnboardingStep(
  sessions: OnboardingSession[],
  sessionId: string,
  stepKey: string,
  actor: string,
  nextStep?: string,
): OnboardingSession[] {
  requireNonEmpty(actor, "actor");
  const step = requireNonEmpty(stepKey, "stepKey");
  return patchSession(sessions, sessionId, (s) => {
    assertWritable(s);
    const completed = s.completedSteps.includes(step)
      ? s.completedSteps
      : [...s.completedSteps, step];
    const reuploadRemaining = (s.reuploadRequestedSteps ?? []).filter(
      (k) => k !== step,
    );
    const stillNeedsReupload = reuploadRemaining.length > 0;
    return {
      ...s,
      completedSteps: completed,
      currentStep: nextStep ?? s.currentStep,
      status:
        s.status === "reupload_requested" && !stillNeedsReupload
          ? "draft"
          : s.status,
      reuploadRequestedSteps: stillNeedsReupload ? reuploadRemaining : undefined,
      lastTouchedAt: nowIso(),
    };
  });
}

export function submitOnboardingSession(
  sessions: OnboardingSession[],
  sessionId: string,
  actor: string,
): OnboardingSession[] {
  requireNonEmpty(actor, "actor");
  return patchSession(sessions, sessionId, (s) => {
    if (s.status === "submitted") return s;
    assertWritable(s);
    const now = nowIso();
    return {
      ...s,
      status: "submitted",
      submittedAt: now,
      lastTouchedAt: now,
    };
  });
}

export function requestReupload(
  sessions: OnboardingSession[],
  sessionId: string,
  stepKeys: string[],
  reason: string,
  actor: string,
): OnboardingSession[] {
  requireNonEmpty(actor, "actor");
  const r = (reason ?? "").trim();
  if (r.length < MIN_REASON_LENGTH) {
    throw new Error(
      `onboardingSessions: reason must be at least ${MIN_REASON_LENGTH} chars.`,
    );
  }
  const steps = (stepKeys ?? []).map((s) => s.trim()).filter(Boolean);
  if (steps.length === 0) {
    throw new Error("onboardingSessions: at least one step is required.");
  }
  return patchSession(sessions, sessionId, (s) => {
    if (s.status === "approved" || s.status === "rejected") {
      throw new Error(
        `onboardingSessions: session ${s.id} is terminal; cannot request reupload.`,
      );
    }
    return {
      ...s,
      status: "reupload_requested" as OnboardingSessionStatus,
      reuploadRequestedSteps: steps,
      reuploadReason: r,
      completedSteps: s.completedSteps.filter((k) => !steps.includes(k)),
      lastTouchedAt: nowIso(),
    };
  });
}

// ── IN-2.g — Admin handoff writers ────────────────────────────────

function requireReason(reason: string): string {
  const r = (reason ?? "").trim();
  if (r.length < MIN_REASON_LENGTH) {
    throw new Error(
      `onboardingSessions: reason must be at least ${MIN_REASON_LENGTH} chars.`,
    );
  }
  return r;
}

/**
 * IN-2.g — Approve a submitted onboarding session.
 *
 * Hard gates:
 *   • Session MUST be in `submitted` state. No skipping draft/reupload.
 *   • Admin readiness gating (verified docs, no blockers) is checked
 *     by the provider via `getAdminApprovalReadiness()`, NOT here —
 *     this writer only enforces the structural state machine.
 *   • Actor must be present and != session.actor of submit (defensive
 *     same-person gate is enforced by the provider with role context).
 *   • Reason ≥ 10 chars.
 *   • Idempotent on `approved` (no-op).
 *
 * P5 collision: this writer DOES NOT touch
 * `Contractor.onboardingCompletedAt`. The portal tour fires on first
 * portal visit after approval (correct UX), via the existing P5
 * resolver. The session itself is the canonical IN-2 approval record.
 */
export function approveOnboardingSession(
  sessions: OnboardingSession[],
  sessionId: string,
  approver: string,
  reason: string,
): OnboardingSession[] {
  requireNonEmpty(approver, "approver");
  const r = requireReason(reason);
  return patchSession(sessions, sessionId, (s) => {
    if (s.status === "approved") return s; // idempotent
    if (s.status !== "submitted") {
      throw new Error(
        `onboardingSessions: cannot approve session ${s.id} in status ${s.status}; must be submitted.`,
      );
    }
    const now = nowIso();
    return {
      ...s,
      status: "approved" as OnboardingSessionStatus,
      approvedAt: now,
      approvedBy: approver,
      approvalReason: r,
      lastTouchedAt: now,
    };
  });
}

/**
 * IN-2.g — Reject a submitted onboarding session.
 *
 * Hard gates:
 *   • Session MUST be in `submitted` or `reupload_requested` state.
 *   • Reason ≥ 10 chars (audit-grade rationale).
 *   • Terminal. Cannot be unset.
 */
export function rejectOnboardingSession(
  sessions: OnboardingSession[],
  sessionId: string,
  rejecter: string,
  reason: string,
): OnboardingSession[] {
  requireNonEmpty(rejecter, "rejecter");
  const r = requireReason(reason);
  return patchSession(sessions, sessionId, (s) => {
    if (s.status === "rejected") return s; // idempotent
    if (s.status !== "submitted" && s.status !== "reupload_requested") {
      throw new Error(
        `onboardingSessions: cannot reject session ${s.id} in status ${s.status}.`,
      );
    }
    const now = nowIso();
    return {
      ...s,
      status: "rejected" as OnboardingSessionStatus,
      rejectedAt: now,
      rejectedBy: rejecter,
      rejectionReason: r,
      lastTouchedAt: now,
    };
  });
}
